import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { GameData, PluginType } from '@meta-press/shared-plugins';
import { PluginEditorBaseComponent } from '../plugin-editor-base/plugin-editor-base.component';
import { SourceMaterialService } from '../source-material-service/source-material.service';

/**
 * Interface for tracking editor hierarchy with parent-child relationships
 */
interface EditorNode {
	/** The editor component instance */
	component: PluginEditorBaseComponent;
	/** The plugin type of this editor */
	pluginType: PluginType;
	/** Display name for this editor type */
	displayName: string;
	/** Nesting level (0 = root, 1 = first child, etc.) */
	nestingLevel: number;
	/** Parent editor node (null for root editors) */
	parent: EditorNode | null;
	/** Child editor nodes */
	children: EditorNode[];
	/** Unique identifier for tracking */
	id: string;
	/** Timestamp when editor was opened */
	openedAt: Date;
}

@Injectable({
	providedIn: 'root',
})
export class NodeEditorService {
	/** Legacy getter for backward compatibility */
	get currentGameData(): GameData | undefined {
		if (this._registeredEditors.length > 0) {
			const gameData = this._registeredEditors[0].GameData;
			return gameData;
		}
		return undefined;
	}

	/** Get the currently active (top-level) editor */
	get currentEditor(): PluginEditorBaseComponent | undefined {
		return this._editorStack.length > 0
			? this._editorStack[this._editorStack.length - 1].component
			: undefined;
	}

	/** Get current nesting level (0-based) */
	get currentNestingLevel(): number {
		return Math.max(0, this._editorStack.length - 1);
	}

	// Legacy array for backward compatibility
	private _registeredEditors: PluginEditorBaseComponent[] = [];

	// New editor hierarchy tracking
	private _editorStack: EditorNode[] = [];
	private _editorMap = new Map<PluginEditorBaseComponent, EditorNode>();
	private _nextId = 1;

	// Observable for real-time breadcrumb updates
	private _breadcrumbSubject = new BehaviorSubject<string>('');
	public breadcrumb$: Observable<string> = this._breadcrumbSubject.asObservable();

	// Observable for nesting level changes
	private _nestingLevelSubject = new BehaviorSubject<number>(0);
	public nestingLevel$: Observable<number> = this._nestingLevelSubject.asObservable();

	// Inject SourceMaterialService for proper display names
	private sourceMaterialService = inject(SourceMaterialService);

	/**
	 * Get the current editor type path as an array
	 * @returns Array of editor type names from root to current
	 */
	public getEditorTypePath(): string[] {
		return this._editorStack.map(node => node.displayName);
	}

	/**
	 * Get formatted breadcrumb string
	 * @param separator Separator between breadcrumb items (default: ' > ')
	 * @param includeNumbering Whether to add numbers for duplicate types (default: false)
	 * @returns Formatted breadcrumb string
	 */
	public getBreadcrumbPath(separator = ' > ', includeNumbering = false): string {
		if (this._editorStack.length <= 1) {
			return ''; // No breadcrumb for single editor
		}

		const path = this.getEditorTypePath();

		if (!includeNumbering) {
			return path.join(separator);
		}

		// Add numbering for duplicate editor types
		const typeCount = new Map<string, number>();
		const numberedPath = path.map(typeName => {
			const count = typeCount.get(typeName) || 0;
			typeCount.set(typeName, count + 1);

			if (count > 0) {
				return `${typeName} ${count + 1}`;
			}
			return typeName;
		});

		return numberedPath.join(separator);
	}

	/**
	 * Get display name for a plugin type using the same logic as EditorTabToolbar
	 * @param pluginType The plugin type
	 * @param component The editor component (for fallback)
	 * @returns Human-readable display name
	 */
	private getPluginDisplayName(pluginType: PluginType, component: PluginEditorBaseComponent): string {
		try {
			// Use the same method as EditorTabToolbar for consistency
			if (pluginType && (pluginType as number) !== PluginType.None) {
				const gameDefinition = this.sourceMaterialService.GetGameTypeSettings(pluginType);
				const displayName = gameDefinition?.title;
				if (displayName) {
					return displayName;
				}

				// Fall back to String(type) like EditorTabToolbar does
				const stringType = String(pluginType);
				if (stringType && stringType !== 'undefined') {
					return stringType;
				}
			}

			// Check if component has a custom display name method
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			if (typeof (component as any).getDisplayName === 'function') {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				return (component as any).getDisplayName();
			}

			// Fall back to enum name for non-None types
			if (pluginType && (pluginType as number) !== PluginType.None) {
				const enumName = PluginType[pluginType];
				if (enumName) {
					return enumName;
				}
			}

			return 'Tuntematon';
		} catch {
			return 'Tuntematon';
		}
	}

	/**
	 * Detect parent-child relationships when registering
	 * @returns Parent editor node if detected, null otherwise
	 */
	private detectParentEditor(): EditorNode | null {
		// The parent is the currently active (last) editor in the stack
		return this._editorStack.length > 0
			? this._editorStack[this._editorStack.length - 1]
			: null;
	}

	/**
	 * Register an editor with hierarchy tracking
	 * @param component The editor component to register
	 */
	public RegisterEditor(component: PluginEditorBaseComponent): void {
		// Maintain backward compatibility
		this._registeredEditors.push(component);

		// Try to get plugin type from EditorGameService first, then component
		let pluginType = component.type;

		// If component type is None or undefined, try to get it from EditorGameService
		if (!pluginType || (pluginType as number) === PluginType.None) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const editorGameService = (component as any).editorGameService;
			if (editorGameService && editorGameService.gameData && editorGameService.gameData.type) {
				pluginType = editorGameService.gameData.type;
			}
		}

		// Fall back to None if still not found
		if (!pluginType) {
			pluginType = PluginType.None;
		}

		const displayName = this.getPluginDisplayName(pluginType, component);
		const parent = this.detectParentEditor();
		const nestingLevel = parent ? parent.nestingLevel + 1 : 0;

		const editorNode: EditorNode = {
			component,
			pluginType,
			displayName,
			nestingLevel,
			parent,
			children: [],
			id: `editor-${this._nextId++}`,
			openedAt: new Date()
		};

		// Add to parent's children if parent exists
		if (parent) {
			parent.children.push(editorNode);
		}

		// Add to stack and map
		this._editorStack.push(editorNode);
		this._editorMap.set(component, editorNode);

		// Update observables
		this._nestingLevelSubject.next(this.currentNestingLevel);
		this._breadcrumbSubject.next(this.getBreadcrumbPath());

		// Debug logging can be enabled here if needed
		// console.log(`[NodeEditorService] Registered editor: ${displayName} at level ${nestingLevel}`);
	}

	/**
	 * Unregister an editor and clean up hierarchy
	 * @param component The editor component to unregister
	 */
	public UnregisterEditor(component: PluginEditorBaseComponent): void {
		// Maintain backward compatibility
		const index = this._registeredEditors.indexOf(component);
		if (index !== -1) {
			this._registeredEditors.splice(index, 1);
		}

		// Handle hierarchy cleanup
		const editorNode = this._editorMap.get(component);
		if (!editorNode) {
			console.warn('[NodeEditorService] Attempted to unregister unknown editor');
			return;
		}

		// Remove from parent's children
		if (editorNode.parent) {
			const childIndex = editorNode.parent.children.indexOf(editorNode);
			if (childIndex !== -1) {
				editorNode.parent.children.splice(childIndex, 1);
			}
		}

		// Handle children - they become orphaned and should be removed from stack
		// In practice, closing a parent editor should close all child editors
		for (const child of editorNode.children) {
			this.removeFromStack(child);
		}

		// Remove from stack and map
		this.removeFromStack(editorNode);
		this._editorMap.delete(component);

		// Update observables
		this._nestingLevelSubject.next(this.currentNestingLevel);
		this._breadcrumbSubject.next(this.getBreadcrumbPath());

		console.log(`[NodeEditorService] Unregistered editor: ${editorNode.displayName}`);
	}

	/**
	 * Remove editor node from stack (recursive for children)
	 * @param node The editor node to remove
	 */
	private removeFromStack(node: EditorNode): void {
		// Remove children first (recursive)
		for (const child of node.children) {
			this.removeFromStack(child);
		}

		// Remove from stack
		const stackIndex = this._editorStack.indexOf(node);
		if (stackIndex !== -1) {
			this._editorStack.splice(stackIndex, 1);
		}

		// Remove from map
		this._editorMap.delete(node.component);

		// Remove from legacy array
		const legacyIndex = this._registeredEditors.indexOf(node.component);
		if (legacyIndex !== -1) {
			this._registeredEditors.splice(legacyIndex, 1);
		}
	}

	/**
	 * Update editor display names when game data becomes available
	 * This is called when an editor's type is properly set after initialization
	 */
	public updateEditorDisplayNames(): void {
		let updated = false;

		for (const node of this._editorStack) {
			// Try to get the correct plugin type from the component's EditorGameService
			let pluginType = node.component.type;

			if (!pluginType || (pluginType as number) === PluginType.None) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const editorGameService = (node.component as any).editorGameService;
				if (editorGameService && editorGameService.gameData && editorGameService.gameData.type) {
					pluginType = editorGameService.gameData.type;
				}
			}

			if (pluginType && (pluginType as number) !== PluginType.None && pluginType !== node.pluginType) {
				// Update the node with the correct plugin type and display name
				node.pluginType = pluginType;
				node.displayName = this.getPluginDisplayName(pluginType, node.component);
				updated = true;
			}
		}

		// Update breadcrumb if anything changed
		if (updated) {
			this._breadcrumbSubject.next(this.getBreadcrumbPath());
		}
	}

	/**
	 * Get debug information about current editor hierarchy
	 * @returns Debug information object
	 */
	public getDebugInfo(): object {
		return {
			stackLength: this._editorStack.length,
			currentNestingLevel: this.currentNestingLevel,
			breadcrumb: this.getBreadcrumbPath(),
			editorPath: this.getEditorTypePath(),
			stack: this._editorStack.map(node => ({
				id: node.id,
				displayName: node.displayName,
				nestingLevel: node.nestingLevel,
				hasParent: !!node.parent,
				childrenCount: node.children.length
			}))
		};
	}
}
